import { Row } from '@tanstack/table-core';

const parse = (val: unknown) => {
    if (!val) return 0;

    if (val instanceof Date) return val.getTime();

    if (typeof val === 'number') return Number.isFinite(val) ? val : 0;

    const time = new Date(val as string).getTime();
    return Number.isNaN(time) ? 0 : time;
};

/**
 * - Factory function that creates a custom sorting function for date strings in TanStack Table.
 * - The returned sorting function compares two rows based on the date values in a specified column.
 * - It safely handles invalid or missing date values by treating them as zero (epoch time).
 *
 * @typeParam T - The type of the table row data
 * @returns - A sorting function that can be used in TanStack Table column definitions to sort date strings chronologically, with invalid or missing dates sorted as the earliest possible date.
 */

export const createDateSortingFn =
    <T>(getValue?: (row: Row<T>, columnId: string) => string | number | Date | null | undefined) =>
    (rowA: Row<T>, rowB: Row<T>, columnId: string) => {
        const a = getValue ? getValue(rowA, columnId) : rowA.getValue(columnId);

        const b = getValue ? getValue(rowB, columnId) : rowB.getValue(columnId);

        return parse(a) - parse(b);
    };
